import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'framer-motion';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { Button } from '../ui';
import { Input } from '../forms';
import { useLogin } from '../../hooks/api';
import { LoginCredentials } from '../../types/api';

// Validation schema
const loginSchema = z.object({
  username: z
    .string()
    .min(1, 'Username is required')
    .max(100, 'Username is too long'),
  password: z
    .string()
    .min(1, 'Password is required'),
  remember_me: z.boolean().optional(),
});

type LoginFormData = z.infer<typeof loginSchema>;

interface LoginFormProps {
  onSuccess?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSuccess }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const loginMutation = useLogin();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: '',
      password: '',
      remember_me: false,
    },
  });

  const watchedFields = watch(['username', 'password']);

  // Clear login error when user starts typing
  useEffect(() => {
    if (loginError && (watchedFields[0] || watchedFields[1])) {
      console.log('Clearing login error due to field change:', { watchedFields, loginError });
      setLoginError(null);
      // Also reset the mutation error state
      loginMutation.reset();
    }
  }, [watchedFields[0], watchedFields[1], loginError]);

  // Handle login mutation errors
  useEffect(() => {
    if (loginMutation.isError) {
      const errorMessage = (loginMutation.error as any)?.response?.data?.detail || 
                          (loginMutation.error as any)?.message ||
                          'Network error';
      setLoginError(errorMessage);
    }
  }, [loginMutation.isError, loginMutation.error]);

  const onSubmit = async (data: LoginFormData) => {
    const credentials: LoginCredentials = {
      username: data.username,
      password: data.password,
    };
    
    loginMutation.mutate(credentials, {
      onSuccess: () => {
        onSuccess?.();
      },
    });
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="w-full max-w-md mx-auto"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Username/Email Field */}
        <div>
          <Input
            {...register('username')}
            label="Username or Email"
            type="text"
            placeholder="Enter your username or email"
            error={errors.username?.message}
            autoComplete="username"
            autoFocus
            disabled={loginMutation.isPending}
          />
        </div>

        {/* Password Field */}
        <div>
          <div className="relative">
            <Input
              {...register('password')}
              label="Password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              error={errors.password?.message}
              autoComplete="current-password"
              disabled={loginMutation.isPending}
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className="absolute right-3 top-[38px] transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
              tabIndex={-1}
              disabled={loginMutation.isPending}
            >
              {showPassword ? (
                <EyeSlashIcon className="w-5 h-5" />
              ) : (
                <EyeIcon className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>

        {/* Remember Me */}
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <input
              {...register('remember_me')}
              id="remember_me"
              type="checkbox"
              className="w-4 h-4 text-primary-600 bg-white border-gray-300 rounded focus:ring-primary-500 focus:ring-2"
              disabled={loginMutation.isPending}
            />
            <label htmlFor="remember_me" className="ml-2 text-sm text-gray-600">
              Remember me
            </label>
          </div>
          <a
            href="/forgot-password"
            className="text-sm text-primary-600 hover:text-primary-500"
          >
            Forgot your password?
          </a>
        </div>

        {/* Error Message */}
        {loginError && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="p-3 bg-red-50 border border-red-200 rounded-lg"
          >
            <p className="text-sm text-red-600">
              {loginError}
            </p>
          </motion.div>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          isLoading={loginMutation.isPending}
          disabled={loginMutation.isPending}
        >
          {loginMutation.isPending ? 'Signing in...' : 'Sign In'}
        </Button>
      </form>
    </motion.div>
  );
};

export default LoginForm;
